"use client";

import { useEffect, useId, useRef } from "react";
import { faceReferencePoints, faceLandmarkEdges } from "@/lib/face-landmark-artwork";

const spacing = 40;
const radius = 180;
const side = 11;
const faceLayout = Object.entries(faceReferencePoints).map(([name, [x, y]]) => ({ name, x: (x - 498) * 0.5, y: (y - 500) * 0.5 }));
const faceIndex = new Map(faceLayout.map((point, index) => [point.name, index]));
const svgNamespace = "http://www.w3.org/2000/svg";
const wrap = (value: number) => (value % side + side) % side;
// Smoothstep mirrors the feathered mask: no brightness or movement seam at the rim.
const influence = (distance: number) => {
  const t = Math.max(0, Math.min(1, (radius - distance) / (radius * 0.45)));
  return t * t * (3 - 2 * t);
};

// The static pattern is server-rendered. Only a small patch near the pointer moves.
export default function MagneticDotField() {
  const id = useId().replaceAll(":", "");
  const svgRef = useRef<SVGSVGElement>(null);
  const holeRef = useRef<SVGCircleElement>(null);
  const patchRef = useRef<SVGGElement>(null);
  const linesRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    const hole = holeRef.current;
    const patch = patchRef.current;
    const lines = linesRef.current;
    const site = svg?.closest<HTMLElement>(".conversion-site");
    if (!svg || !hole || !patch || !lines || !site || typeof window.matchMedia !== "function" || typeof ResizeObserver === "undefined") return;
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = matchMedia("(hover: hover) and (pointer: fine)");
    let frame = 0;
    let bounds = site.getBoundingClientRect();
    let pointer: { x: number; y: number } | null = null;
    let target: { x: number; y: number } | null = null;
    let current: { x: number; y: number } | null = null;
    let strength = 0;
    let faceStrength = 0;
    let dwellStarted = 0;
    let dwellAnchor: { x: number; y: number } | null = null;
    let anchorRevision = 0;
    let assignmentRevision = -1;
    let faceSources: { column: number; row: number; key: string }[] = [];
    let previousTime = 0;
    const dots = Array.from({ length: side * side }, () => {
      const circle = document.createElementNS(svgNamespace, "circle");
      circle.setAttribute("r", "0.7");
      patch.appendChild(circle);
      return { circle, key: "", x: 0, y: 0, size: 0.7, opacity: 0 };
    });

    function clear() {
      cancelAnimationFrame(frame);
      frame = 0;
      pointer = target = current = null;
      strength = faceStrength = dwellStarted = previousTime = 0;
      dwellAnchor = null;
      faceSources = [];
      assignmentRevision = -1;
      dots.forEach(dot => { dot.key = ""; });
      hole!.setAttribute("r", "0");
      patch!.setAttribute("visibility", "hidden");
      lines!.setAttribute("d", "");
    }

    function draw(time: number) {
      frame = 0;
      if (!target || !current) return;
      const elapsed = previousTime ? Math.min(time - previousTime, 50) : 0;
      const follow = 1 - Math.exp(-elapsed / 90);
      const ease = 1 - Math.exp(-elapsed / 110);
      previousTime = time;
      current.x += (target.x - current.x) * follow;
      current.y += (target.y - current.y) * follow;
      strength += (1 - strength) * (1 - Math.exp(-elapsed / 380));
      if (!dwellStarted) dwellStarted = time;
      const dwellProgress = Math.max(0, Math.min(1, (time - dwellStarted - 1100) / 1700));
      // Finish releasing the previous face before a new one can gather.
      const faceTarget = assignmentRevision === anchorRevision
        ? dwellProgress * dwellProgress * (3 - 2 * dwellProgress)
        : 0;
      faceStrength += (faceTarget - faceStrength) * (1 - Math.exp(-elapsed / 175));
      const x = current.x;
      const y = current.y;
      hole!.setAttribute("cx", String(x));
      hole!.setAttribute("cy", String(y));
      hole!.setAttribute("r", String(radius));
      patch!.setAttribute("visibility", "visible");
      const originColumn = Math.round((x - 1) / spacing);
      const originRow = Math.round((y - 1) / spacing);
      const grid: { column: number; row: number; key: string; x: number; y: number; dx: number; dy: number; distance: number }[] = [];
      for (let row = originRow - 5; row <= originRow + 5; row++) for (let column = originColumn - 5; column <= originColumn + 5; column++) {
        const baseX = column * spacing + 1;
        const baseY = row * spacing + 1;
        grid.push({ column, row, key: `${column}:${row}`, x: baseX, y: baseY, dx: baseX - x, dy: baseY - y, distance: Math.hypot(baseX - x, baseY - y) });
      }
      // Choose sources only once the patch reaches the pointer. Assigning while
      // it is travelling locks dots from the old patch that later leave the grid.
      if (assignmentRevision !== anchorRevision && faceStrength < 0.01 && Math.hypot(target.x - x, target.y - y) < 1) {
        const candidates = new Set(grid.filter(dot => dot.distance >= 110 && dot.distance < radius));
        faceSources = faceLayout.map(landmark => {
          let nearest: typeof grid[number] | undefined;
          let score = Infinity;
          for (const dot of candidates) {
            const angle = Math.atan2(dot.dy, dot.dx) - Math.atan2(landmark.y, landmark.x);
            const cost = 1 - Math.cos(angle) + dot.distance / 1000;
            if (cost < score) { nearest = dot; score = cost; }
          }
          if (nearest) candidates.delete(nearest);
          return nearest!;
        });
        assignmentRevision = anchorRevision;
      }
      const assignments = new Map(faceSources.map((source, index) => [source.key, index]));
      const rendered = new Map<string, { x: number; y: number }>();
      let unsettled = false;
      for (const dot of grid) {
        // A grid coordinate always owns the same slot, even across cell boundaries.
        // Recycled slots are outside the influence area, where the mask is transparent.
        const state = dots[wrap(dot.row) * side + wrap(dot.column)];
        if (state.key !== dot.key) {
          state.key = dot.key;
          state.x = dot.x;
          state.y = dot.y;
          state.opacity = 0.25 * influence(dot.distance);
          state.size = 0.7;
          state.circle.setAttribute("data-grid-key", dot.key);
        }
        const landmarkIndex = assignments.get(dot.key);
        const edge = influence(dot.distance);
        const gathering = (landmarkIndex === undefined ? 0.65 : 0.5) * strength * Math.max(0, 1 - dot.distance / radius) * edge;
        let drawnX = dot.x - dot.dx * gathering;
        let drawnY = dot.y - dot.dy * gathering;
        if (landmarkIndex !== undefined) {
          const landmark = faceLayout[landmarkIndex];
          const sourceAngle = Math.atan2(dot.dy, dot.dx);
          const destinationAngle = Math.atan2(landmark.y, landmark.x);
          const turn = Math.atan2(Math.sin(destinationAngle - sourceAngle), Math.cos(destinationAngle - sourceAngle));
          const angle = sourceAngle + turn * faceStrength;
          const gatheredRadius = dot.distance * (1 - gathering);
          const distance = gatheredRadius + (Math.hypot(landmark.x, landmark.y) - gatheredRadius) * faceStrength;
          drawnX = x + Math.cos(angle) * distance;
          drawnY = y + Math.sin(angle) * distance;
        }
        const blend = influence(Math.hypot(drawnX - x, drawnY - y));
        const facial = landmarkIndex === undefined ? 0 : faceStrength;
        const opacity = (0.45 + strength * 0.18 * blend + facial * 0.22) * blend;
        const size = 0.7 + (strength * 0.25 + facial * 0.3) * blend;
        // Bound travel as well as easing so a dropped frame cannot cause a hop.
        const travel = Math.hypot(drawnX - state.x, drawnY - state.y);
        const positionEase = travel ? Math.min(ease, elapsed * 0.26 / travel) : ease;
        state.x += (drawnX - state.x) * positionEase;
        state.y += (drawnY - state.y) * positionEase;
        state.opacity += (opacity - state.opacity) * ease;
        state.size += (size - state.size) * ease;
        if (Math.hypot(drawnX - state.x, drawnY - state.y) > 0.1 || Math.abs(opacity - state.opacity) > 0.002) unsettled = true;
        state.circle.setAttribute("cx", String(state.x));
        state.circle.setAttribute("cy", String(state.y));
        state.circle.setAttribute("r", String(state.size));
        state.circle.setAttribute("opacity", String(state.opacity));
        state.circle.setAttribute("visibility", dot.distance < radius || state.opacity > 0.002 ? "visible" : "hidden");
        rendered.set(dot.key, state);
      }
      const segments = faceLandmarkEdges.flatMap(([a, b]) => {
        const start = rendered.get(faceSources[faceIndex.get(a)!]?.key);
        const end = rendered.get(faceSources[faceIndex.get(b)!]?.key);
        return start && end ? [`M${start.x},${start.y}L${end.x},${end.y}`] : [];
      });
      lines!.setAttribute("d", segments.join(" "));
      lines!.setAttribute("stroke-opacity", String(faceStrength * 0.35));
      if (unsettled || Math.hypot(target.x - current.x, target.y - current.y) > 0.1 || strength < 0.99 || faceTarget < 1 || Math.abs(faceStrength - faceTarget) > 0.005) frame = requestAnimationFrame(draw);
    }

    function retarget() {
      if (!pointer) return;
      // Animation state uses site coordinates, shared with the static dot grid.
      target = { x: pointer.x - bounds.left, y: pointer.y - bounds.top };
      if (!dwellAnchor || Math.hypot(target.x - dwellAnchor.x, target.y - dwellAnchor.y) > 18) {
        dwellAnchor = { ...target };
        dwellStarted = performance.now();
        anchorRevision++;
      }
      if (!current) current = { ...target };
      if (!frame) frame = requestAnimationFrame(draw);
    }
    function move(event: PointerEvent) {
      if (event.pointerType !== "mouse" || motion.matches || !finePointer.matches) return;
      pointer = { x: event.clientX, y: event.clientY };
      retarget();
    }
    function refreshBounds() {
      const next = site!.getBoundingClientRect();
      const moved = next.top !== bounds.top || next.left !== bounds.left;
      if (next.width !== bounds.width) clear();
      bounds = next;
      // A stationary pointer crosses the site's grid when the document scrolls.
      if (moved) retarget();
    }
    function mediaChanged() { if (motion.matches || !finePointer.matches) clear(); }
    const resize = new ResizeObserver(refreshBounds);
    resize.observe(site);
    site.addEventListener("pointermove", move, { passive: true });
    site.addEventListener("pointerleave", clear);
    window.addEventListener("scroll", refreshBounds, { passive: true });
    window.addEventListener("blur", clear);
    motion.addEventListener("change", mediaChanged);
    finePointer.addEventListener("change", mediaChanged);
    return () => {
      clear();
      dots.forEach(dot => dot.circle.remove());
      resize.disconnect();
      site.removeEventListener("pointermove", move);
      site.removeEventListener("pointerleave", clear);
      window.removeEventListener("scroll", refreshBounds);
      window.removeEventListener("blur", clear);
      motion.removeEventListener("change", mediaChanged);
      finePointer.removeEventListener("change", mediaChanged);
    };
  }, []);

  return <svg ref={svgRef} className="conversion-backdrop" fill="none" aria-hidden="true">
    <defs>
      <pattern id={`${id}-dots`} width={spacing} height={spacing} patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="0.7" fill="currentColor" /></pattern>
      <radialGradient id={`${id}-feather`}><stop offset="55%" stopColor="black" /><stop offset="65%" stopColor="black" stopOpacity="0.874" /><stop offset="75%" stopColor="black" stopOpacity="0.583" /><stop offset="85%" stopColor="black" stopOpacity="0.259" /><stop offset="95%" stopColor="black" stopOpacity="0.034" /><stop offset="100%" stopColor="black" stopOpacity="0" /></radialGradient>
      <mask id={`${id}-mask`} maskUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%" style={{ maskType: "luminance" }}>
        <rect width="100%" height="100%" fill="white" /><circle ref={holeRef} r="0" fill={`url(#${id}-feather)`} />
      </mask>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id}-dots)`} mask={`url(#${id}-mask)`} className="conversion-field-dots" />
    <path ref={linesRef} className="conversion-magnetic-lines" />
    <g ref={patchRef} className="conversion-magnetic-dots" visibility="hidden" />
  </svg>;
}
